import * as React from 'react';
import styled, { css } from 'styled-components';
import {
  DatePicker,
  DatePickerChangeEvent,
} from '@progress/kendo-react-dateinputs';
import { DateFormatOptions } from '@progress/kendo-react-intl';
import SimpleReactValidator from 'simple-react-validator';
import CustomCalendar from './CustomCalendar';
import CalendarIcon from './assets/CalenderIcon.svg';
import { RectangleSkeletonLoader } from '../SkeletonLoader/RectangleSkeletonLoader';

export type DatePickerProps = {
  onChange?: (event: DatePickerChangeEvent) => void;
  children?: React.ReactNode;
  disabled?: boolean;
  defaultValue?: Date;
  title?: string;
  dateFormat?: string | DateFormatOptions;
  customLabel?: 'primary';
  inputPaddingBlock?: 'primary';
  value?: Date | null;
  minDate?: Date;
  maxDate?: Date;
  validator?: SimpleReactValidator;
  validations?: string;
  customValidationMsg?: any;
  fieldType?: 'type1' | 'type2' | 'type3';
  permissionId?: string;
  disableValidation?: boolean;
  className?: string;
  requestFormType?: boolean;
  hideStar?: boolean;
  isLoader?: boolean;
  customErrorMsg?: string;
  disablePlaceHolder?: string;
  customPlaceHolder?: string;
  iscustomErrorMsg?: boolean;
  customErrorMsgText?: string;
  label?: string;
};

interface WrapperProps extends DatePickerProps {
  background?: 'primary' | 'secondary' | 'tertiary';
  border?: 'primary' | 'secondary' | 'none' | 'error';
}

export const CustomDatepicker = ({
  defaultValue,
  title,
  disabled,
  dateFormat,
  onChange,
  customLabel,
  inputPaddingBlock,
  maxDate,
  value,
  minDate,
  validations,
  validator,
  fieldType,
  className,
  hideStar,
  requestFormType = false,
  isLoader,
  customErrorMsg,
  disablePlaceHolder,
  iscustomErrorMsg,
  customErrorMsgText,
  customValidationMsg,
  customPlaceHolder,
  label,
}: DatePickerProps) => {
  const simpleValidator = React.useRef(validator);
  const dateFormatRequired = dateFormat ? dateFormat : 'dd/MM/yyyy';
  const datePickerRef = React.useRef<HTMLDivElement>(null);
  const [render, setRender] = React.useState(true);
  const [pickerValue, setPickerValue] = React.useState<Date | null>();
  const [dateParts, setDateParts] = React.useState({
    day: '',
    month: '',
    year: '',
  });

  let background: 'primary' | 'secondary' | 'tertiary';
  let border: 'primary' | 'secondary' | 'none' | 'error';
  if (fieldType === 'type2') {
    background = 'secondary';
    border = 'primary';
  } else if (fieldType === 'type3') {
    background = 'tertiary';
    border = 'secondary';
  } else {
    background = 'primary';
    border = 'primary';
  }

  const constructDate = (
    day: string,
    month: string,
    year: string,
  ): Date | null => {
    if (!day || !month || !year) return null;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return !isNaN(date.getTime()) ? date : null;
  };
  const parseInputDate = (input: string): Date | null => {
    const normalized = input.replace(/[-.\s]/g, '/');
    const parts = normalized.split('/');
    if (parts.length === 3) {
      let [day, month, year] = parts;
      day = day.padStart(2, '0');
      month = month.padStart(2, '0');

      // Handle year parsing more intelligently
      if (year.length === 1 || year.length === 3) {
        // Don't process partial year inputs (1 or 3 digits)
        return null;
      } else if (year.length === 2) {
        // Assume 20xx for 2-digit years
        year = `20${year}`;
      } else if (year.length === 4) {
        // Use as-is for 4-digit years
        year = year;
      } else {
        // Invalid year length
        return null;
      }

      return constructDate(day, month, year);
    }
    return null;
  };

  const handleDateChange = (event: DatePickerChangeEvent) => {
    const newValue = event.value;

    // Always update the internal picker value to allow typing
    setPickerValue(newValue);

    // Check if it's a Date object with a reasonable year
    if (newValue instanceof Date && !isNaN(newValue.getTime())) {
      const year = newValue.getFullYear();

      // Only accept years in a reasonable range (1900-2100)
      if (year >= 1900 && year <= 2100) {
        const newDay = newValue.getDate().toString().padStart(2, '0');
        const newMonth = (newValue.getMonth() + 1).toString().padStart(2, '0');
        const newYear = newValue.getFullYear().toString();

        setDateParts({ day: newDay, month: newMonth, year: newYear });
        onChange?.(event);
        return;
      } else {
        // Don't call parent onChange, but allow internal typing to continue
        return;
      }
    }

    const inputValue = (event.target as any)?.value || '';
    const parsedDate = parseInputDate(inputValue);

    if (parsedDate && !isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear();

      // Same validation for parsed dates
      if (year >= 1900 && year <= 2100) {
        const newDay = parsedDate.getDate().toString().padStart(2, '0');
        const newMonth = (parsedDate.getMonth() + 1)
          .toString()
          .padStart(2, '0');
        const newYear = parsedDate.getFullYear().toString();

        setDateParts({ day: newDay, month: newMonth, year: newYear });
        onChange?.({ ...event, value: parsedDate });
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedData = e.clipboardData.getData('Text').trim();
    const parsedDate = parseInputDate(pastedData);
    if (parsedDate) {
      e.preventDefault();
      setPickerValue(parsedDate);
      setDateParts({
        day: parsedDate.getDate().toString().padStart(2, '0'),
        month: (parsedDate.getMonth() + 1).toString().padStart(2, '0'),
        year: parsedDate.getFullYear().toString(),
      });
      onChange?.({
        syntheticEvent: e as any,
        nativeEvent: e.nativeEvent,
        value: parsedDate,
        target: {
          name: title ?? '',
          value: parsedDate,
        },
      } as DatePickerChangeEvent);
    }
  };

  const handleBlur = () => {
    if (validator) {
      simpleValidator?.current?.showMessageFor(title ? title : 'field');
      setRender(!render);
    }
  };

  const handleClick = (e: any) => {
    const wrapperElement = e.target.closest('.custom-calendar-wrapper');
    if (wrapperElement?.length !== 0) {
      handleBlur();
    }
  };

  React.useEffect(() => {
    if (value) {
      setPickerValue(value);
      setDateParts({
        day: value.getDate().toString().padStart(2, '0'),
        month: (value.getMonth() + 1).toString().padStart(2, '0'),
        year: value.getFullYear().toString(),
      });
    } else if (defaultValue) {
      setPickerValue(defaultValue);
      setDateParts({
        day: defaultValue.getDate().toString().padStart(2, '0'),
        month: (defaultValue.getMonth() + 1).toString().padStart(2, '0'),
        year: defaultValue.getFullYear().toString(),
      });
    } else {
      setPickerValue(null);
      setDateParts({ day: '', month: '', year: '' });
    }
  }, [value, defaultValue]);

  const errorMessage = simpleValidator?.current?.message(
    title ? title : 'field',
    value?.toString(),
    validations ?? '',
  );
  const isError = errorMessage;

  return (
    <Wrapper
      background={background}
      border={!value ? 'error' : border}
      customLabel={customLabel}
      className={`custom-calendar-wrapper ${className}`}
      disabled={disabled}
    >
      <>
        <DateWrapper
          className={`${requestFormType && 'requestForm'} cardViewStyle`}
        >
          <InputTextStyle tabIndex={0}>
            <LabelWrapper
              className={`${
                requestFormType && 'requestForm'
              } date-picker-label`}
            >
              <div className="date-picker-title">
                {label ? label : title}
                <span className="red-star">
                  {validations && !hideStar ? ' *' : ''}
                </span>
              </div>
            </LabelWrapper>
            {isLoader ? (
              <RectangleSkeletonLoader
                className="field-skeleton"
                count={1}
                height={40}
              />
            ) : (
              <StyleDatePicker
                ref={datePickerRef}
                className={`datepicker-class ${
                  value ? 'active' : 'non-active'
                } ${requestFormType && 'requestForm'} ${
                  disabled ? 'disabled' : ''
                }`}
                calendar={CustomCalendar}
                inputPaddingBlock={inputPaddingBlock}
                value={pickerValue}
                disabled={disabled}
                format={dateFormatRequired}
                weekNumber={true}
                onChange={handleDateChange}
                onBlur={handleClick}
                onPaste={handlePaste}
                background={background}
                border={border}
                placeholder={
                  disabled && disablePlaceHolder
                    ? disablePlaceHolder
                    : customPlaceHolder
                    ? customPlaceHolder
                    : 'DD/MM/YYYY'
                }
                max={maxDate}
                min={minDate}
              />
            )}
          </InputTextStyle>
        </DateWrapper>
        <Errortext className="error-text">
          {customErrorMsg
            ? customErrorMsg
            : isError
            ? simpleValidator?.current?.message(
                title ? title : 'field',
                value?.toString(),
                validations ?? '',
                customValidationMsg,
              )
            : iscustomErrorMsg && <>{customErrorMsgText}</>}
        </Errortext>
      </>
    </Wrapper>
  );
};

// Keep existing styled components
const inputBlock = {
  primary: css`
    margin: 10px 4px 0px 0px;
  `,
};
const Wrapper = styled.div<WrapperProps>`
  width: 100%;
  &.add-payment-component {
    min-height: 90px;
    .k-icon-button {
      background-color: ${p => p.theme.addPaymentInputClr};
      border-left: 1px solid ${p => p.theme.addPaymentDatePickerBorderClr};
      padding-left: 10px;
    }
    .k-datepicker {
      background-color: ${p => p.theme.addPaymentInputClr};
      border: unset !important;
    }
    .date-picker-title {
      font-size: 12px;
      font-family: 'Open Sans';
      font-weight: 600;
      color: ${p => p.theme.addPaymentLabeltClr};
      margin-left: 0px;
    }
  }
  &.edit-loan-component {
    min-height: 90px;
    @media (max-width: 768px) {
      min-height: 120px;
    }
  }
  &.add-facility-component {
    min-height: 90px;
  }
  &.add-lc-component {
    min-height: 90px;
  }
  &.add-sub-limit-component {
    min-height: 90px;
  }
  &.requestForm {
    min-height: 40px;
  }

  .datepicker-class {
    border: 1px solid
      ${props =>
        props.disabled
          ? p => p.theme.disabledBrdrClr
          : p => p.theme.datePickerBorderClr} !important;
  }
  .red-star {
    color: ${p => p.theme.errorStarRed};
    font-weight: 400;
  }

  :has(input:disabled) {
    .red-star {
      display: none !important;
    }
  }
  .date-picker-title {
    font-size: 14px;
    font-family: 'Open Sans';
    font-weight: 400;
    color: ${p => p.theme.datePickerTitle};
    line-height: 19.6px;
  }
  .non-active {
    .k-input-inner {
      color: ${p => p.theme.datePickerNonActiveInputClr} !important;
      font-size: 12px;
      font-weight: 500;
      font-family: 'Open Sans';
      line-height: 18.15px;

      ::placeholder {
        color: ${p => p.theme.dropDownPlaceholderColor};
        font-weight: 400 !important;
        font-family: 'Open Sans';
        font-size: 12px !important;
      }
    }
  }

  .k-input-solid {
    padding-left: 20px;
    align-items: center;
    height: 40px;
    background: ${props =>
      props.disabled ? p => p.theme.disabledBgClr : p => p.theme.white};
  }
  &.requestForm {
    margin: 0;
  }
`;
const StyleDatePicker = styled(DatePicker)<WrapperProps>`
  &.requestForm {
    input {
      font-weight: 500;
      font-size: 12px;
    }
  }
  width: 100%;
  border: none;
  font-size: 12px !important;
  border-radius: 5px;
  font-family: 'Open Sans';
  left: 0px;
  &:focus,
  &:focus-within {
    outline-style: none;
    box-shadow: none;
  }
  .k-button-solid-base ::before {
    content: '';
    width: 15px;
    height: 15px;
    background-image: url(${CalendarIcon});
    background-size: 16px;
    background-size: contain;
    position: absolute;
    top: 8px;
  }
  .k-svg-icon > svg {
    opacity: 0;
  }
  .k-button {
    position: relative;
  }
  .k-button-md.k-icon-button > .k-button-icon {
    height: 30px;
    z-index: 9;
  }
  .k-dateinput {
    .k-input-inner {
      padding: 0;
      font-family: 'Open Sans';
      font-size: 12px;
      font-weight: 600;
      line-height: 18.15px;
      ${props =>
        props.inputPaddingBlock && inputBlock[props.inputPaddingBlock]};
      color: ${p => p.theme.datepickerInnerClr};
    }
  }
  .k-button-solid-base {
    margin: 0px 0px 0px 4px;
    padding: 0 18px 0 0;
    background-color: ${p => p.theme.white};
    border-inline-start-width: 0px;
  }
  &.requestForm {
    .k-button-solid-base {
      background-color: ${p => p.theme.white};
    }
    background-color: ${p => p.theme.white};
  }
  .k-i-calendar::before {
    content: '';
    background-image: url(${CalendarIcon});
    width: 22px;
    height: 25px;
  }
  &.disabled {
    .k-button-icon {
      display: none;
      border-color: ${p => p.theme.disabledBrdrClr} !important;
      background-color: ${p => p.theme.disabledBgClr} !important;
    }
    border: none;
  }
  .k-disabled {
    input {
      color: ${p => p.theme.dropDownPlaceholderColor} !important;
    }
  }
  //Responsive
  @media (min-width: 100px) and (max-width: 319px) {
    .date-picker-title {
      font-size: 10px;
    }
    .k-dateinput {
      .k-input-inner {
        font-size: 11px;
      }
    }
    .non-active {
      .k-input-inner {
        font-size: 11px;
      }
    }
  }
  @media (min-width: 320px) and (max-width: 575px) {
    .date-picker-title {
      font-size: 11px;
    }
    .k-dateinput {
      .k-input-inner {
        font-size: 12px;
      }
    }
    .non-active {
      .k-input-inner {
        font-size: 12px;
      }
    }
  }
  @media (min-width: 576px) and (max-width: 767px) {
    .date-picker-title {
      font-size: 12px;
    }
    .k-dateinput {
      .k-input-inner {
        font-size: 12px;
      }
    }
    .non-active {
      .k-input-inner {
        font-size: 13px;
      }
    }
  }
  @media (min-width: 768px) and (max-width: 1200px) {
    .date-picker-title {
      font-size: 13px;
    }
    .k-dateinput {
      .k-input-inner {
        font-size: 12px;
      }
    }
    .non-active {
      .k-input-inner {
        font-size: 14px;
      }
    }
  }
`;
const InputTextStyle = styled.span`
  .k-disabled {
    opacity: 1;
    color: ${p => p.theme.datepickerInputSpan};
  }
  .date-picker-label {
    label {
      font-weight: 500;
    }
    &.requestForm {
      margin-bottom: 0;
    }
  }
`;
const LabelWrapper = styled.div`
  margin-bottom: 5px;
  margin-left: 4px;
`;
const DateWrapper = styled.div`
  .cardViewStyle {
    width: 100%;
    border: none;
    background: ${p => p.theme.white};
  }
  &.requestForm {
    margin-bottom: 0;
  }
`;
const Errortext = styled.span`
  display: block;
  font-size: 13px !important;
  color: ${p => p.theme.errorStarRed};
  padding-top: 4px !important;
  text-align: left;
  font-family: 'Open Sans';
`;
